import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';

/** The only two fields the grid search reads. */
export type AssetSearchFields = Pick<ImageAssetType, 'imageID' | 'caption'>;

const ALL_DIGITS = /^\d+$/;

/**
 * DV-2856 grid search (pure, so the grids, the select-all reducers and the
 * download-selection math all narrow by exactly the same rule — a second copy
 * is how the checkbox and the grid start disagreeing).
 *
 * All-digit input substring-matches the Image ID; anything else substring-matches
 * the caption, case-insensitively. Substring rather than prefix (DV-3335)
 * because the useful query is often a trailing run ("234" → 20000234) — what a
 * user reads off a photo or a support ticket. The cost is that short queries
 * match interior coincidences; that is accepted, because this is a live filter
 * over one property's photos and one more digit narrows it.
 *
 * Two deliberate behaviours from the AC and the backend handoff:
 * - mixed digits-and-letters is caption text (IDs match all-digit input only);
 * - `imageID == null` ("not stamped yet") never matches an ID search, so a
 *   pre-backfill photoset doesn't answer every digit query with everything.
 *
 * This is a FILTER, never a find-first lookup: IDs copied from Legacy are not
 * unique (a photo migrated twice yields two assets with the same ID), so every
 * match must stay visible.
 */
export function matchesAssetQuery(
  asset: AssetSearchFields,
  query: string
): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;

  if (ALL_DIGITS.test(trimmed)) {
    if (asset.imageID == null) return false;
    return String(asset.imageID).includes(trimmed);
  }

  return (asset.caption ?? '').toLowerCase().includes(trimmed.toLowerCase());
}
